import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface FormField {
  id: string;
  label: string;
  kind: "text" | "email" | "number" | "textarea" | "select" | "checkbox" | "tel";
  required?: boolean;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
}

interface FormStep {
  id: string;
  title: string;
  description?: string;
  fields: FormField[];
}

interface FormSchema {
  id: string;
  title: string;
  description?: string;
  version: string;
  firestoreCollection: string;
  steps: FormStep[];
}

interface DynamicFormRemoteProps {
  slug: string;
}

export default function DynamicFormRemote({ slug }: DynamicFormRemoteProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchSchema();
  }, [slug]);

  const fetchSchema = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "form_schemas", slug);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError(`Form schema "${slug}" not found`);
        return;
      }

      const schemaData = docSnap.data() as FormSchema;
      setSchema(schemaData);
      setError(null);
    } catch (err) {
      console.error("Error fetching schema:", err);
      setError("Failed to load form schema");
    } finally {
      setLoading(false);
    }
  };

  const buildZodSchema = (fields: FormField[]) => {
    const schemaObj: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
      let fieldSchema: z.ZodTypeAny;

      switch (field.kind) {
        case "email":
          fieldSchema = z.string().email("Invalid email address");
          break;
        case "number":
          fieldSchema = z.number();
          if (field.min !== undefined) {
            fieldSchema = (fieldSchema as z.ZodNumber).min(field.min);
          }
          if (field.max !== undefined) {
            fieldSchema = (fieldSchema as z.ZodNumber).max(field.max);
          }
          break;
        case "checkbox":
          fieldSchema = z.boolean();
          if (field.required) {
            fieldSchema = fieldSchema.refine((val) => val === true, {
              message: "This field is required",
            });
          }
          break;
        case "tel":
          fieldSchema = z.string().min(10, "Phone must be at least 10 characters");
          break;
        default:
          fieldSchema = z.string();
          if (field.min !== undefined) {
            fieldSchema = (fieldSchema as z.ZodString).min(field.min);
          }
          if (field.max !== undefined) {
            fieldSchema = (fieldSchema as z.ZodString).max(field.max);
          }
          break;
      }

      if (!field.required && field.kind !== "checkbox") {
        fieldSchema = fieldSchema.optional();
      } else if (field.required && field.kind !== "checkbox") {
        fieldSchema = (fieldSchema as z.ZodString).min(1, "This field is required");
      }

      schemaObj[field.id] = fieldSchema;
    });

    return z.object(schemaObj);
  };

  const getDefaultValues = (fields: FormField[]) => {
    const defaults: Record<string, any> = {};
    fields.forEach((field) => {
      if (field.kind === "checkbox") {
        defaults[field.id] = false;
      } else if (field.kind === "number") {
        defaults[field.id] = field.min || 0;
      } else {
        defaults[field.id] = "";
      }
    });
    return defaults;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-destructive">{error || "Form not found"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStepData = schema.steps[currentStep];
  const totalSteps = schema.steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {submitted ? (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
              <h2 className="text-2xl font-bold">Form Submitted Successfully!</h2>
              <p className="text-muted-foreground">
                Thank you for completing the form. Your response has been recorded.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">{schema.title}</h1>
              {schema.description && (
                <p className="text-muted-foreground">{schema.description}</p>
              )}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>
                    Step {currentStep + 1} of {totalSteps}
                  </span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} />
              </div>
            </div>

            <StepForm
              step={currentStepData}
              schema={schema}
              currentStepIndex={currentStep}
              totalSteps={totalSteps}
              onNext={() => setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1))}
              onPrev={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              onSubmit={async (data) => {
                if (!user) {
                  toast({
                    title: "Authentication Required",
                    description: "Please log in to submit this form",
                    variant: "destructive",
                  });
                  return;
                }

                setIsSubmitting(true);
                try {
                  await addDoc(collection(db, schema.firestoreCollection), {
                    ...data,
                    userId: user.id,
                    userEmail: user.email,
                    formId: schema.id,
                    formVersion: schema.version,
                    createdAt: serverTimestamp(),
                    status: "submitted",
                  });

                  toast({
                    title: "Success",
                    description: "Form submitted successfully",
                  });

                  setSubmitted(true);
                } catch (error) {
                  console.error("Error submitting form:", error);
                  toast({
                    title: "Error",
                    description: "Failed to submit form. Please try again.",
                    variant: "destructive",
                  });
                } finally {
                  setIsSubmitting(false);
                }
              }}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </div>
    </div>
  );
}

interface StepFormProps {
  step: FormStep;
  schema: FormSchema;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: (data: any) => Promise<void>;
  isSubmitting: boolean;
}

function StepForm({
  step,
  schema,
  currentStepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSubmit,
  isSubmitting,
}: StepFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});

  const buildZodSchema = (fields: FormField[]) => {
    const schemaObj: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
      let fieldSchema: z.ZodTypeAny;

      switch (field.kind) {
        case "email":
          fieldSchema = z.string().email("Invalid email address").max(255);
          break;
        case "number":
          fieldSchema = z.number();
          if (field.min !== undefined) {
            fieldSchema = (fieldSchema as z.ZodNumber).min(field.min);
          }
          if (field.max !== undefined) {
            fieldSchema = (fieldSchema as z.ZodNumber).max(field.max);
          }
          break;
        case "checkbox":
          fieldSchema = z.boolean();
          if (field.required) {
            fieldSchema = fieldSchema.refine((val) => val === true, {
              message: "This field is required",
            });
          }
          break;
        case "tel":
          fieldSchema = z.string().min(10, "Phone must be at least 10 characters").max(20);
          break;
        default:
          fieldSchema = z.string().max(field.max || 1000);
          if (field.min !== undefined) {
            fieldSchema = (fieldSchema as z.ZodString).min(field.min);
          }
          break;
      }

      if (!field.required && field.kind !== "checkbox") {
        fieldSchema = fieldSchema.optional();
      } else if (field.required && field.kind !== "checkbox") {
        if (field.kind === "number") {
          // number fields handle required differently
        } else {
          fieldSchema = (fieldSchema as z.ZodString).min(1, "This field is required");
        }
      }

      schemaObj[field.id] = fieldSchema;
    });

    return z.object(schemaObj);
  };

  const getDefaultValues = (fields: FormField[]) => {
    const defaults: Record<string, any> = {};
    fields.forEach((field) => {
      if (formData[field.id] !== undefined) {
        defaults[field.id] = formData[field.id];
      } else if (field.kind === "checkbox") {
        defaults[field.id] = false;
      } else if (field.kind === "number") {
        defaults[field.id] = field.min || 0;
      } else {
        defaults[field.id] = "";
      }
    });
    return defaults;
  };

  const form = useForm({
    resolver: zodResolver(buildZodSchema(step.fields)),
    defaultValues: getDefaultValues(step.fields),
  });

  const handleNext = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const values = form.getValues();
      setFormData((prev) => ({ ...prev, ...values }));
      onNext();
    }
  };

  const handleSubmit = async (data: any) => {
    const allData = { ...formData, ...data };
    await onSubmit(allData);
  };

  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{step.title}</CardTitle>
        {step.description && <CardDescription>{step.description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(isLastStep ? handleSubmit : handleNext)}
            className="space-y-6"
          >
            {step.fields.map((field) => (
              <FormField
                key={field.id}
                control={form.control}
                name={field.id}
                render={({ field: formField }) => (
                  <FormItem>
                    <FormLabel>
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </FormLabel>
                    <FormControl>
                      {field.kind === "select" && field.options ? (
                        <Select
                          onValueChange={formField.onChange}
                          defaultValue={formField.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={field.placeholder || "Select an option"} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.kind === "textarea" ? (
                        <Textarea
                          {...formField}
                          placeholder={field.placeholder}
                          rows={4}
                        />
                      ) : field.kind === "checkbox" ? (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={formField.value}
                            onCheckedChange={formField.onChange}
                          />
                          <span className="text-sm">{field.description}</span>
                        </div>
                      ) : field.kind === "number" ? (
                        <Input
                          type="number"
                          {...formField}
                          onChange={(e) => formField.onChange(Number(e.target.value))}
                          placeholder={field.placeholder}
                        />
                      ) : (
                        <Input
                          type={field.kind}
                          {...formField}
                          placeholder={field.placeholder}
                        />
                      )}
                    </FormControl>
                    {field.description && field.kind !== "checkbox" && (
                      <FormDescription>{field.description}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}

            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onPrev}
                disabled={currentStepIndex === 0}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>

              {isLastStep ? (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              ) : (
                <Button type="submit">
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
